import React, { useState } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram,
  ArrowUp,
  Building,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import hulacapitalWhite from '../assets/logo/hulacapitalwhite.png';

const Footer = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const footerLinks = {
    services: [
      { name: 'Blockchain-based Assets', href: '/sectors/blockchain', isRoute: true },
      { name: 'Real Estate', href: '/sectors/real-estate', isRoute: true },
      { name: 'Stock Trading', href: '/sectors/stocks', isRoute: true },
      { name: 'Index Funds', href: '/sectors/indices', isRoute: true },
      { name: 'Risk Management', href: '/services', isRoute: true }
    ],
    company: [
      { name: 'About Us', href: '/about', isRoute: true },
      { name: 'Contact Us', href: '/contact', isRoute: true }
    ],
    resources: [
      { name: 'FAQ', href: '/faq', isRoute: true }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy-policy', isRoute: true },
      { name: 'Terms of Service', href: '/terms-of-service', isRoute: true }
    ]
  };

  const socialLinks = [];

  const contactInfo = [
    {
      icon: Mail,
      content: 'info@hulacapital.com'
    },
    {
      icon: Phone,
      content: '+447562727621'
    },
    {
      icon: MapPin,
      content: '10th Floor 19 Sun St, London, EC2A 2FJ, UK'
    }
  ];

  const FooterSection = ({ title, links, sectionKey }) => (
    <div className="border-b border-white/10 lg:border-b-0">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex justify-between items-center py-4 lg:cursor-default"
      >
        <h4 className="text-lg font-semibold text-gold-400 lg:mb-6">{title}</h4>
        <div className="lg:hidden">
          {expandedSection === sectionKey ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>
      <div className={`${
        expandedSection === sectionKey || window.innerWidth >= 1024 ? 'block' : 'hidden'
      } lg:block pb-4 lg:pb-0`}>
        <ul className="space-y-2 lg:space-y-3">
          {links.map((link, index) => (
            <li key={index}>
              {link.isRoute ? (
                <Link
                  to={link.href}
                  className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm block py-1"
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  href={link.href}
                  className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm block py-1"
                >
                  {link.name}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <footer className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1 mb-6 lg:mb-0">
            <div className="mb-4 lg:mb-6">
              <img
                src={hulacapitalWhite}
                alt="HULA CAPITAL LIMITED"
                className="h-6 lg:h-8 w-auto mb-3 lg:mb-4"
              />
            </div>
            
            <p className="text-gray-300 mb-4 lg:mb-6 leading-relaxed text-sm lg:text-base">
              Your trusted partner in financial management and investment advisory. 
              Registered in England & Wales (Company No. 16534250).
            </p>

            {/* Companies House Link - Hidden on mobile */}
            <div className="mb-4 lg:mb-6 hidden lg:block">
              <a
                href="https://find-and-update.company-information.service.gov.uk/company/16534250"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 hover:border-gold-400 transition-all duration-300 text-xs lg:text-sm text-gray-300 hover:text-gold-400"
              >
                <Building className="h-3 w-3 lg:h-4 lg:w-4 mr-2" />
                View Company Registration
              </a>
            </div>

            {/* Contact Info - Compact on mobile */}
            <div className="space-y-2 lg:space-y-3">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <div key={index} className="flex items-start space-x-2 lg:space-x-3">
                    <IconComponent className="h-3 w-3 lg:h-4 lg:w-4 text-gold-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-xs lg:text-sm leading-tight">{info.content}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile: Collapsible Sections, Desktop: Regular Layout */}
          <div className="lg:hidden">
            <FooterSection title="Services" links={footerLinks.services} sectionKey="services" />
            <FooterSection title="Company" links={footerLinks.company} sectionKey="company" />
            <FooterSection title="Resources" links={footerLinks.resources} sectionKey="resources" />
            <FooterSection title="Legal" links={footerLinks.legal} sectionKey="legal" />
          </div>

          {/* Desktop: Regular Grid Layout */}
          <div className="hidden lg:block">
            <h4 className="text-lg font-semibold mb-6 text-gold-400">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden lg:block">
            <h4 className="text-lg font-semibold mb-6 text-gold-400">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden lg:block space-y-8">
            <div>
              <h4 className="text-lg font-semibold mb-6 text-gold-400">Resources</h4>
              <ul className="space-y-3">
                {footerLinks.resources.map((link, index) => (
                  <li key={index}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6 text-gold-400">Legal</h4>
              <ul className="space-y-3">
                {footerLinks.legal.map((link, index) => (
                  <li key={index}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-gray-300 hover:text-gold-400 transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <div className="text-gray-400 text-xs lg:text-sm text-center md:text-left">
              © 2024 HULA CAPITAL LIMITED. All rights reserved.
              <span className="hidden lg:inline ml-1">| Licensed Financial Services Provider</span>
            </div>
            
            <button
              onClick={scrollToTop}
              className="w-8 h-8 lg:w-10 lg:h-10 bg-gold-500 hover:bg-gold-600 hover:scale-110 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg"
              aria-label="Scroll to top"
            >
              <ArrowUp className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Disclaimer - Simplified on mobile */}
      <div className="bg-navy-950 py-3 lg:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            <strong>Risk Warning:</strong> Trading involves substantial risk. Past performance is not indicative of future results.
            <span className="hidden lg:inline"> Please ensure you fully understand the risks involved and seek independent advice if necessary. HULA CAPITAL LIMITED is regulated and authorized to provide investment services.</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 